// Calculation utilities for checklist components

export interface CalculationInputs {
  length?: number;
  width?: number;
  height?: number;
  depth?: number;
  [key: string]: number | undefined;
}

/**
 * Parse calculation formula and evaluate with given inputs
 * @param formula - Mathematical formula string (e.g., "length * width / 1000000")
 * @param inputs - Object containing input values
 * @returns Calculated result or null if inputs are missing
 */
export function evaluateCalculation(formula: string, inputs: CalculationInputs): number | null {
  try {
    // Ensure default values for optional wall dimensions (wall5-wall10)
    const inputsWithDefaults: CalculationInputs = { ...inputs };
    for (let i = 1; i <= 10; i++) {
      if (inputsWithDefaults[`wall${i}Length`] === undefined) {
        inputsWithDefaults[`wall${i}Length`] = 0;
      }
      if (inputsWithDefaults[`wall${i}Height`] === undefined) {
        inputsWithDefaults[`wall${i}Height`] = 0;
      }
    }

    // Check if all required variables in the formula have values
    const variables = formula.match(/[a-zA-Z_]\w*/g) || [];
    const hasAllInputs = variables.every((variable) => {
      const value = inputsWithDefaults[variable];
      return value !== undefined && value !== null && !isNaN(value);
    });

    if (!hasAllInputs) {
      console.warn("Missing inputs for formula:", {
        formula,
        variables,
        inputs: inputsWithDefaults,
        missing: variables.filter(v => {
          const val = inputsWithDefaults[v];
          return val === undefined || val === null || isNaN(val);
        })
      });
      return null;
    }

    // Create a safe evaluation function using Function constructor
    // Replace variable names with their values
    let evaluableFormula = formula;
    Object.entries(inputsWithDefaults).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        // Use word boundaries to avoid partial replacements
        const regex = new RegExp(`\\b${key}\\b`, "g");
        evaluableFormula = evaluableFormula.replace(regex, String(value));
      }
    });

    // Evaluate the formula
    // eslint-disable-next-line no-new-func
    const result = Function(`'use strict'; return (${evaluableFormula})`)();

    if (typeof result === "number" && isFinite(result)) {
      return result;
    }

    return null;
  } catch (error) {
    console.error("Calculation error:", error, { formula, inputs });
    return null;
  }
}

/**
 * Format calculated value with unit
 * @param value - Calculated numerical value
 * @param unit - Display unit (e.g., "m²", "m³")
 * @param decimalPlaces - Number of decimal places to show
 * @returns Formatted string
 */
export function formatCalculatedValue(
  value: number | null,
  unit?: string,
  decimalPlaces = 2
): string {
  if (value === null || isNaN(value)) {
    return "—";
  }

  const formatted = value.toFixed(decimalPlaces);
  return unit ? `${formatted} ${unit}` : formatted;
}

/**
 * Parse calculation inputs from JSON string
 * @param inputsJson - JSON string array of input field names
 * @returns Array of input field names
 */
export function parseCalculationInputs(inputsJson?: string | null): string[] {
  if (!inputsJson) return [];

  try {
    const parsed = JSON.parse(inputsJson);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn("Failed to parse calculation inputs:", inputsJson, error);
    return [];
  }
}

/**
 * Common calculation formulas
 */
export const COMMON_FORMULAS = {
  // Area calculations (mm to m²)
  area_mm_to_m2: "length * width / 1000000",
  // Volume calculations (mm to m³)
  volume_mm_to_m3: "length * width * height / 1000000000",
  // Linear meter calculations (mm to m)
  linear_mm_to_m: "length / 1000",
  // Wall area with height (mm to m²)
  wall_area_mm_to_m2: "((length + width) * 2) * height / 1000000",
  // Tile count estimate (area / tile area with 10% waste)
  tile_count: "(length * width / (tile_length * tile_width)) * 1.1",
} as const;

/**
 * Get user-friendly label for calculation input
 * @param inputName - Input field name (e.g., "length", "width")
 * @returns Display label
 */
export function getInputLabel(inputName: string): string {
  const labels: Record<string, string> = {
    length: "Length (mm)",
    width: "Width (mm)",
    height: "Height (mm)",
    depth: "Depth (mm)",
    tile_length: "Tile Length (mm)",
    tile_width: "Tile Width (mm)",
    floorLength: "Floor Length (m)",
    floorWidth: "Floor Width (m)",
    wastagePercent: "Wastage (%)",
    includeFloor: "Include Floor",
    numWalls: "Number of Walls",
    tileBoxCoverage: "Tile Box Coverage (m²)",
    tileSize: "Tile Size",
  };

  // Handle dynamic wall labels (wall1Length, wall1Height, wall2Length, etc.)
  const wallMatch = inputName.match(/^wall(\d+)(Length|Height)$/);
  if (wallMatch) {
    const wallNumber = wallMatch[1];
    const dimension = wallMatch[2];
    return `Wall ${wallNumber} ${dimension} (m)`;
  }

  return labels[inputName] || inputName;
}
